'use client';

import React, { useMemo, useState } from 'react';
import { TabbedContentProps } from './types';
import { HeadlineWrapper } from '@/components/headline-wrapper/HeadlineWrapper';
import clsx from 'clsx';
import componentFactory from '@/lib/component-factory';

const TabbedContent = ({
  contentTab,
  sectionId,
  headlineWrapper,
  locale,
  theme = 'light',
}: TabbedContentProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabs = useMemo(
    () =>
      contentTab.filter((tab) => tab?.tabName && Array.isArray(tab?.content)),
    [contentTab],
  );
  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
  };
  return (
    <section
      id={sectionId}
      className={clsx('content-tab-switcher relative pt-12', {
        'bg-white': theme === 'light',
        'bg-brand-100': theme === 'dark',
      })}
    >
      {headlineWrapper && <HeadlineWrapper {...headlineWrapper} />}
      <div className="mx-auto flex flex-col gap-10 lg:gap-14">
        <div className="mt-6 w-full overflow-x-auto">
          <ul
            className="mx-auto flex min-w-max justify-center"
            role="tablist"
            aria-label="Content tabs"
          >
            {tabs.map((tab, index) => (
              <li
                key={`${tab.tabName}-${index}`}
                role="tab"
                tabIndex={index === activeTabIndex ? 0 : -1}
                aria-selected={index === activeTabIndex}
                onClick={() => handleTabChange(index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleTabChange(index);
                  }
                }}
                className={clsx(
                  'cursor-pointer whitespace-nowrap border-b-2 px-6 pb-4 text-lg font-medium text-brand-700',
                  {
                    'border-brand-300 opacity-50': index !== activeTabIndex,
                  },
                  {
                    'border-brand-500': index === activeTabIndex,
                  },
                )}
              >
                {tab.tabName}
              </li>
            ))}
          </ul>
        </div>

        {tabs.map((tab, tabIndex) => (
          <div
            key={`content-tab-panel-${tabIndex}`}
            role="tabpanel"
            className={tabIndex === activeTabIndex ? 'block' : 'hidden'}
          >
            <div className="flex flex-col gap-8">
              {tab.content.map((section, index) => (
                <React.Fragment
                  key={`content-tab-section-${tabIndex}-${index}`}
                >
                  {componentFactory({ section, index, locale })}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default TabbedContent;
